import { useState } from "react";
import { Phone, Mail, MapPin, Send } from "lucide-react";
import useSeo from "@/hooks/useSeo";
import { Link } from "react-router-dom";

export default function Contact() {
  useSeo({
    title: "Contact | Full Indoor Air Services",
    description: "Get in touch with Full Indoor Air Services for a free quote on air duct cleaning across MD, DC, and VA.",
    canonical: "https://capitalindoorair.com/contact",
  });

  const [submitted, setSubmitted] = useState(false);

  return (
    <div className="bg-bone" data-testid="contact-page">
      <section className="section-pad">
        <div className="max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-12 gap-12">
          <div className="lg:col-span-5">
            <div className="eyebrow mb-3">Get in touch</div>
            <h1 className="font-heading text-5xl md:text-6xl tracking-tighter font-light">
              Talk to a real <span className="text-forest">human.</span>
            </h1>
            <p className="mt-6 text-muted2 leading-relaxed text-lg">
              Call, text, email — or use the form. We answer within a few hours, Mon–Sat.
            </p>

            <div className="mt-10 space-y-5">
              <a href="tel:+18003737324" className="flex items-center gap-4 group" data-testid="contact-phone">
                <span className="inline-flex w-11 h-11 rounded-full bg-forest/10 text-forest items-center justify-center group-hover:bg-forest group-hover:text-white transition">
                  <Phone size={18} />
                </span>
                <div>
                  <div className="font-heading text-xl">1-800-FRESH-AIR</div>
                  <div className="text-xs text-muted2 uppercase tracking-[0.22em]">Mon–Sat · 7am–8pm</div>
                </div>
              </a>
              <a href="mailto:hello@capitalindoorair.com" className="flex items-center gap-4 group" data-testid="contact-email-link">
                <span className="inline-flex w-11 h-11 rounded-full bg-forest/10 text-forest items-center justify-center group-hover:bg-forest group-hover:text-white transition">
                  <Mail size={18} />
                </span>
                <div>
                  <div className="font-heading text-xl">hello@capitalindoorair.com</div>
                  <div className="text-xs text-muted2 uppercase tracking-[0.22em]">Replies within hours</div>
                </div>
              </a>
              <div className="flex items-center gap-4">
                <span className="inline-flex w-11 h-11 rounded-full bg-forest/10 text-forest items-center justify-center">
                  <MapPin size={18} />
                </span>
                <div>
                  <div className="font-heading text-xl">MD · Washington DC · VA</div>
                  <div className="text-xs text-muted2 uppercase tracking-[0.22em]">9 cities & growing</div>
                </div>
              </div>
            </div>
          </div>

          <div className="lg:col-span-7">
            <div className="card-surface p-8 md:p-10" data-testid="contact-card">
              {submitted ? (
                <div className="text-center py-10" data-testid="contact-success">
                  <h2 className="font-heading text-3xl tracking-tight">Thanks — we'll be in touch.</h2>
                  <p className="mt-4 text-muted2">For instant scheduling, you can also <Link to="/book" className="text-forest underline">book online</Link>.</p>
                </div>
              ) : (
                <form
                  onSubmit={(e) => { e.preventDefault(); setSubmitted(true); }}
                  className="space-y-5"
                  data-testid="contact-form"
                >
                  <div className="eyebrow">Quick quote request</div>
                  <h2 className="font-heading text-3xl tracking-tight">Tell us about your space.</h2>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <input className="input-base" placeholder="Full name" required data-testid="contact-name" />
                    <input className="input-base" type="email" placeholder="Email" required data-testid="contact-email" />
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <input className="input-base" placeholder="Phone" required data-testid="contact-phone-input" />
                    <input className="input-base" placeholder="City (MD, DC, or VA)" required data-testid="contact-city" />
                  </div>
                  <textarea
                    className="input-base min-h-[140px]"
                    placeholder="How many vents? Any concerns? When works for you?"
                    data-testid="contact-message"
                  />
                  <button type="submit" className="btn-primary w-full justify-center" data-testid="contact-submit">
                    Send message <Send size={14} />
                  </button>
                  <p className="text-xs text-muted2 text-center">
                    By submitting you agree to be contacted by Full Indoor Air Services.
                  </p>
                </form>
              )}
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
